/**
 * ORBIS Cloud Dashboard - Internationalization Configuration
 */

import { createI18n } from 'vue-i18n'
import es from './locales/es/translation.json'
import en from './locales/en/translation.json'

export type SupportedLocale = 'es' | 'en'

export const SUPPORTED_LOCALES: SupportedLocale[] = ['es', 'en']
export const DEFAULT_LOCALE: SupportedLocale = 'es'
const STORAGE_KEY = 'orbis-admin-locale'

/**
 * Detect browser language preference
 */
function detectBrowserLocale(): SupportedLocale {
  const browserLang = navigator.language || (navigator as any).userLanguage || ''
  
  // Check for exact match first
  const exactMatch = browserLang.toLowerCase()
  if (SUPPORTED_LOCALES.includes(exactMatch as SupportedLocale)) {
    return exactMatch as SupportedLocale
  }
  
  // Check for language code prefix (e.g., 'en-US' -> 'en')
  const langCode = browserLang.split('-')[0].toLowerCase()
  if (SUPPORTED_LOCALES.includes(langCode as SupportedLocale)) {
    return langCode as SupportedLocale
  }
  
  return DEFAULT_LOCALE
}

/**
 * Get the initial locale:
 * 1. Check localStorage for user preference
 * 2. Fall back to browser detection
 * 3. Fall back to default (Spanish)
 */
function getInitialLocale(): SupportedLocale {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && SUPPORTED_LOCALES.includes(stored as SupportedLocale)) {
    return stored as SupportedLocale
  }
  
  return detectBrowserLocale()
}

/**
 * Save locale preference to localStorage
 */
export function saveLocale(locale: SupportedLocale): void {
  localStorage.setItem(STORAGE_KEY, locale)
}

/**
 * Get current locale
 */
export function getCurrentLocale(): SupportedLocale {
  return i18n.global.locale.value as SupportedLocale
}

/**
 * Get display name for a locale
 */
export function getLocaleDisplayName(locale: SupportedLocale): string {
  const names: Record<SupportedLocale, string> = {
    es: 'Español',
    en: 'English',
  }
  return names[locale]
}

/**
 * Get flag emoji for a locale
 */
export function getLocaleFlag(locale: SupportedLocale): string {
  const flags: Record<SupportedLocale, string> = {
    es: '🇨🇱',
    en: '🇺🇸',
  }
  return flags[locale]
}

const i18n = createI18n({
  legacy: false,
  locale: getInitialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages: {
    es,
    en,
  },
  warnHtmlMessage: false,
})

export default i18n

/**
 * Change the current locale
 */
export function setLocale(locale: SupportedLocale): void {
  i18n.global.locale.value = locale
  saveLocale(locale)
}

